import { Badge, HStack, Stack, Text } from "@chakra-ui/react";
import type { Holiday } from "@/api/Holiday";
import type { CustomHoliday } from "@/api/CustomHoliday";
import { Tooltip } from "@/components/ui/tooltip";
import { useI18n } from "@/i18n/I18nContext";

export const HolidayDetails = ({ holiday, rule, compact = false }: {
  holiday: Holiday;
  rule?: CustomHoliday;
  compact?: boolean;
}) => {
  const { t } = useI18n();
  const source = holiday.override ? "override" : holiday.custom ? "custom" : "public";
  const duration = holiday.lengthInDays === 0.5 ? "halfDay" : "fullDay";
  const label = `${holiday.name} · ${t(`publicHolidays.${source}`)} · ${t(`publicHolidays.${duration}`)}`;

  return <Stack gap="1">
    <HStack gap="1" wrap="wrap">
      <Tooltip content={label}>
        <Badge tabIndex={0} aria-label={label} colorPalette={source === "override" ? "orange" : source === "custom" ? "purple" : "green"}>
          {compact ? "●" : t(`publicHolidays.${source}`)}
        </Badge>
      </Tooltip>
      {!compact && <Text fontSize="xs">{t(`publicHolidays.${duration}`)}</Text>}
    </HStack>
    {rule && <HolidayRuleDetails rule={rule} />}
  </Stack>;
};

export const HolidayRuleDetails = ({ rule }: { rule: CustomHoliday }) => {
  const { language, t } = useI18n();
  const locale = language === "en" ? "en-GB" : "de-CH";
  const date = (value: string) => new Date(`${value}T00:00:00`).toLocaleDateString(locale);
  const recurrence = () => {
    const r = rule.dateRule;
    switch (r.type) {
      case "once": return t("publicHolidays.once", { date: date(r.date!) });
      case "annual_date": return t("publicHolidays.annual", {
        date: new Date(2000, r.month! - 1, r.day!).toLocaleDateString(locale, { day: "numeric", month: "long" }),
      });
      case "nth_weekday": return t("publicHolidays.nthWeekday", {
        ordinal: t(`publicHolidays.ordinal${r.ordinal}`),
        weekday: new Date(2024, 0, r.weekday!).toLocaleDateString(locale, { weekday: "long" }),
        month: new Date(2000, r.month! - 1, 1).toLocaleDateString(locale, { month: "long" }),
      });
      default: return "";
    }
  };

  return <Text fontSize="xs" color="fg.muted">
    {recurrence()} · {rule.localValidUntil
      ? t("publicHolidays.validBetween", { from: date(rule.localValidFrom), until: date(rule.localValidUntil) })
      : t("publicHolidays.validFrom", { from: date(rule.localValidFrom) })}
  </Text>;
};
